import { useId, useState, type ReactElement } from 'react';
import { signInWithProvider } from '@/core/auth';
import { config, SOCIAL_PROVIDERS, type SocialProvider } from '@/core/config';
import { cn, Spinner } from '@/core/ui';
import { Hourglass, WarningCircle } from '@/core/ui/icons';
import type { InAppBrowser } from '../domain/in-app-browser';
import { OpenInBrowser } from './OpenInBrowser';

/**
 * "Continuar com o Google" / "Continuar com a Apple".
 *
 * Os dois botões aparecem sempre; só redirecionam os provedores listados em
 * `VITE_SOCIAL_LOGIN` (conectados no Keycloak pelo configure-realm.sh). Os
 * outros avisam, na própria tela, que ainda não estão disponíveis.
 *
 * Visual conforme as diretrizes de cada marca — por isso fogem dos tokens
 * do tema. Do Google, a variante "neutra" das diretrizes (fundo #F2F2F2,
 * texto #1F1F1F, sem contorno) — a única sem borda, como o resto do app; a
 * Apple pede o botão branco no fundo escuro e preto no claro, com o logo na
 * cor do texto. Textos oficiais em português de cada marca.
 *
 * Dentro de um app (Instagram, Facebook, WhatsApp… — `inApp`), o Google
 * recusa o login (`disallowed_useragent`): o botão não redireciona; explica
 * e oferece abrir a página no navegador.
 */
export function SocialLogin({
  returnTo,
  inApp = null,
  className,
}: {
  returnTo?: string;
  inApp?: InAppBrowser | null;
  className?: string;
}) {
  const [pending, setPending] = useState<SocialProvider | null>(null);
  const [notice, setNotice] = useState<{
    provider: SocialProvider;
    kind: 'unavailable' | 'error' | 'in-app';
  } | null>(null);
  const noticeId = useId();

  async function start(provider: SocialProvider) {
    if (!config.auth.socialProviders.includes(provider)) {
      setNotice({ provider, kind: 'unavailable' });
      return;
    }
    if (inApp) {
      setNotice({ provider, kind: 'in-app' });
      return;
    }
    setNotice(null);
    setPending(provider);
    try {
      // Sai da página: o Keycloak manda para o provedor e volta por /auth/callback.
      await signInWithProvider(provider, returnTo);
    } catch {
      setPending(null);
      setNotice({ provider, kind: 'error' });
    }
  }

  return (
    <div className={cn('space-y-3', className)}>
      {SOCIAL_PROVIDERS.map((provider) => {
        const brand = BRANDS[provider];
        return (
          <button
            key={provider}
            type="button"
            onClick={() => void start(provider)}
            disabled={pending !== null}
            aria-describedby={notice?.provider === provider ? noticeId : undefined}
            className={cn(
              'relative flex h-12 w-full items-center justify-center gap-3 rounded-full px-5 text-[0.95rem] font-medium',
              'transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.99] disabled:cursor-wait',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400',
              brand.className,
            )}
          >
            {pending === provider ? <Spinner className="size-5" /> : <brand.Logo />}
            {brand.label}
          </button>
        );
      })}

      {notice?.kind === 'in-app' && inApp && (
        <OpenInBrowser id={noticeId} inApp={inApp} provider={BRANDS[notice.provider].name} />
      )}

      {notice && notice.kind !== 'in-app' && (
        <p
          id={noticeId}
          role="status"
          className={cn(
            'flex items-start gap-2 rounded-2xl p-3 text-sm',
            notice.kind === 'error'
              ? 'bg-red-500/10 text-red-300 light:text-red-700'
              : 'bg-surface-2 text-fg-muted',
          )}
        >
          {notice.kind === 'error' ? (
            <WarningCircle aria-hidden weight="fill" className="mt-0.5 size-4 shrink-0" />
          ) : (
            <Hourglass aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-300" />
          )}
          {notice.kind === 'error'
            ? `Não foi possível abrir o login com ${BRANDS[notice.provider].name}. Tente novamente.`
            : `O login com ${BRANDS[notice.provider].name} ainda não está disponível. Entre com seu e-mail e senha.`}
        </p>
      )}
    </div>
  );
}

/** Separador entre o login social e o formulário. */
export function OrSeparator({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-3 text-xs font-medium text-fg-subtle', className)}>
      <span aria-hidden className="h-px flex-1 bg-surface-3" />
      ou
      <span aria-hidden className="h-px flex-1 bg-surface-3" />
    </div>
  );
}

const BRANDS: Record<
  SocialProvider,
  { name: string; label: string; className: string; Logo: () => ReactElement }
> = {
  google: {
    name: 'Google',
    label: 'Continuar com o Google',
    className: 'bg-[#f2f2f2] text-[#1f1f1f]',
    Logo: GoogleLogo,
  },
  apple: {
    name: 'Apple',
    label: 'Continuar com a Apple',
    className: 'bg-white text-black light:bg-black light:text-white',
    Logo: AppleLogo,
  },
};

/** O "G" de quatro cores do Google — nunca recolorido. */
function GoogleLogo() {
  return (
    <svg aria-hidden viewBox="0 0 48 48" className="size-5 shrink-0">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** A maçã da Apple, na cor do texto do botão. */
function AppleLogo() {
  return (
    <svg aria-hidden viewBox="0 0 814 1000" fill="currentColor" className="size-5 shrink-0 -translate-y-px">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}
